import { computed, defineAsyncComponent, markRaw } from 'vue';
import { injectLocal, provideLocal } from '@vueuse/core';
import { sanitize } from '@vue-util/sanitize';
import { useUpc } from '@modules/upc';
import { useBrand } from '@composables/useBrand';
import { useProductType } from '@composables/useProductType';
import { useSlideout } from '@composables/utils/useSlideout';
import { useExperiments } from '@composables/useExperiments';
import { useProduct } from '@modules/product';
import { useProductFlags } from '@modules/featureControl';
import { PRODUCT_DETAILS_REPOSITORY } from '../constants';

export const useProductDetails = () => injectLocal(PRODUCT_DETAILS_REPOSITORY);

export const useProductDetailsProvide = () => {
  const { productData } = useProduct();
  const { isConsolidatedStandard } = useProductType();
  const { showSlideout } = useSlideout();
  const { selectedUpcId, upcData } = useUpc();
  const { processedProdDescFlag } = useProductFlags();
  const { isBcom } = useBrand();
  const { experimentFlags: { productDetailsDrawer, productDetailsTreatment2 } } = useExperiments();

  /** @returns {Product['detail'] | undefined} */
  function _getProductDetails() {
    return isConsolidatedStandard && selectedUpcId.value
      ? productData.relationships?.upcs?.[selectedUpcId.value]?.detail
      : productData.detail;
  }

  /** @returns {import('vue').ComputedRef<Product['detail']>} */
  const productDetails = computed(_getProductDetails);

  /** @returns {string} */
  function _getProductDescription() {
    const description = productDetails.value?.description?.trim();

    if ((productDetailsDrawer || productDetailsTreatment2) && description?.endsWith('<br><br>FEATURES')) {
      return sanitize(description?.split('<br><br>FEATURES')[0]);
    }
    return sanitize(description || '');
  }

  /**
   * @typedef {Object} BulletItem
   * @property {string} type
   * @property {string} text
   * @returns {BulletItem[]}
   * */
  function _getProductSummaryBullets() {
    const textBullets = processedProdDescFlag
      ? (productDetails.value?.processedProdDesc?.productDetails || [])
      : (productDetails.value?.bulletText || []);

    const mappedTextBullets = textBullets.map((bullet) => ({ type: 'TEXT', text: sanitize(bullet) }));
    const { bulletLinks } = productDetails.value || { bulletLinks: [] };
    return [...mappedTextBullets, ...bulletLinks];
  }

  /** @returns {string} */
  function _getProductDimensionsTitle() {
    return sanitize(productDetails?.value?.dimensionsHeaderText || '');
  }

  /** @returns {string[]} */
  function _getProductDimensionsBullets() {
    const bullets = productDetails?.value?.dimensionsBulletText || [];
    return bullets.map(sanitize);
  }

  /** @returns {BulletItem[]} */
  function _getMaterialsAndCareBullets() {
    const { processedProdDesc, materialsAndCare } = productDetails?.value || {};
    const bullets = materialsAndCare || processedProdDesc?.fabricAndCare || [];

    const mappedTextBullets = bullets.map((bullet) => ({ type: 'TEXT', text: sanitize(bullet) }));

    const hasHiddenFillDisclosure = false; // backend flag, change it
    if (hasHiddenFillDisclosure) {
      const additionalBullet = { type: 'LABEL_DISCLOSURE', text: 'Product labels for all quilted items (filled apparel, home textiles, and accessories) are available online.' };
      return [...mappedTextBullets, additionalBullet];
    }

    return mappedTextBullets;
  }

  /** @returns {string[]} */
  function _getSizeAndFitBullets() {
    const dimensionsBulletsLocal = _getProductDimensionsBullets();
    const { processedProdDesc } = productDetails?.value || {};

    return isBcom
      ? (dimensionsBulletsLocal || [])
      : (processedProdDesc?.sizeAndFit || []).map(sanitize);
  }

  function openProductFillLegalDisclosureDrawer() {
    showSlideout({
      id: 'product-fill-legal-disclosure',
      title: 'Product Fill Legal Disclosure',
      props: { availableUpcs: Object.keys(upcData.value.upcs ?? {}) },
      component: markRaw(defineAsyncComponent(/* istanbul ignore next */ () => import(/* webpackChunkName: "[request]" */ '../components/slideouts/ProductFillLegalDisclosureDrawer'))),
    });
  }

  provideLocal(PRODUCT_DETAILS_REPOSITORY, {
    openProductFillLegalDisclosureDrawer,
    availableUpcs: Object.keys(upcData.value.upcs ?? {}),
    productDescription: computed(_getProductDescription),
    productSummaryBullets: computed(_getProductSummaryBullets),
    dimensionsTitle: _getProductDimensionsTitle(),
    dimensionsBullets: _getProductDimensionsBullets(),
    materialsAndCareBullets: _getMaterialsAndCareBullets(),
    sizeAndFit: _getSizeAndFitBullets(),
  });
};
